import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConsentChoicesFields } from '@/components/consent/ConsentChoicesFields';
import { useAuth } from '@/hooks/useAuth';
import { choicesFromSaved, defaultChoices, fetchMyConsents, needsAsking, saveMyConsents, type ConsentChoices } from '@/lib/consents';

/**
 * Asked once of anyone signed in with no recorded answer: people who signed
 * up before these choices existed, or with Google (no sign-up form). Closing
 * it without answering asks again next visit; nothing is assumed.
 * Asked once more of people who answered before the DeepSeek box existed
 * (D60), with their earlier answers filled in.
 */
export function FirstSignInChoices() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>(defaultChoices);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [askingAgain, setAskingAgain] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    fetchMyConsents()
      .then((saved) => {
        if (!active || !needsAsking(saved)) return;
        if (saved?.answered) {
          setChoices(choicesFromSaved(saved));
          setAskingAgain(true);
        }
        setOpen(true);
      })
      .catch(() => { /* asked again next visit */ });
    return () => { active = false; };
  }, [user?.id]);

  const save = async (next: ConsentChoices) => {
    setBusy(true);
    setFailed(false);
    try {
      await saveMyConsents(next, 'first_sign_in');
      if (next.transferAbroad) setOpen(false);
      else setDeclined(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (!user?.id) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) setOpen(false); }}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-2xl p-5 sm:p-6">
        <DialogHeader className="pr-10 text-left">
          <DialogTitle className="leading-snug">Your choices about messages and your data</DialogTitle>
          <DialogDescription>
            {askingAgain
              ? 'We have added one optional choice, about DeepSeek. Your other answers are filled in. You can change these any time in your account.'
              : 'We now ask everyone. You can change these any time in your account.'}
          </DialogDescription>
        </DialogHeader>
        {declined ? (
          <p role="status" className="text-sm">
            Noted. Without this agreement Kanfy cannot keep your account, because it is stored with these providers.
            Support will help you close it, or you can agree later in your account.
          </p>
        ) : (
          <ConsentChoicesFields value={choices} onChange={setChoices} disabled={busy} idPrefix="first-consent" />
        )}
        {failed && <p role="alert" className="text-sm text-destructive">That didn’t save. Please try again.</p>}
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-start">
          {declined ? (
            <Button onClick={() => setOpen(false)}>Close</Button>
          ) : (
            <>
              <Button disabled={busy || !choices.transferAbroad} onClick={() => save(choices)}>Save my choices</Button>
              <Button variant="outline" disabled={busy} onClick={() => save({ ...choices, transferAbroad: false })}>
                I don’t agree to processing abroad
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
